import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import colors from "../styles/colors";
import { getProductImageByType } from "../services/image_services";
import ContactUsSection from "../components/HomeSectionContainers/ContactUsSectoin";
import PortfolioSection from "../components/HomeSectionContainers/PortfolioSection";
import { useTranslation } from "react-i18next";
import { PiCaretCircleRightLight } from "react-icons/pi";
import SEO from "../utils/SEO";
import { SlArrowDown } from "react-icons/sl";

const Products: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "auto";
    };
  }, []);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

  const productTypes = [
    { key: "windows", path: "windows" },
    { key: "entrDoors", path: "entrance-doors" },
    { key: "intDoors", path: "interior-doors" },
    { key: "slidingDoors", path: "sliding-doors" },
    { key: "winterGarden", path: "winter-garden" },
    { key: "accessories", path: "accessories" },
  ];

  const scrollDown = () => {
    // Az oldal lefelé görgetése 600px-t
    window.scrollBy({ top: 700, left: 0, behavior: "smooth" });
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center font-serif overflow-x-hidden"
      style={{ backgroundColor: colors.pastelCream }}
    >
      {/* SEO meta tags */}
      <SEO
        title={t("seo_meta.products.metaTitle")}
        description={t("seo_meta.products.metaDescription")}
      />

      {/* Title Section */}
      <motion.div
        className="h-[80vh] lg:h-screen w-full flex flex-col items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: "url('/assets/images/productsPage_images/wood_bg2.webp')",
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Sötétítő réteg  */}
        <div className="h-[80vh] lg:h-screen absolute inset-0 bg-black opacity-30"></div>
        {/* title */}
        <motion.h1
          className="lg:text-8xl text-3xl md:text-5xl mt-24 md:mt-48 z-10 whitespace-nowrap"
          style={{ color: colors.lightCream }}
          initial={{ y: 35, opacity: 0 }}
          whileInView={{
            y: 0,
            opacity: 1,
            transition: { delay: 0.2, duration: 0.6 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          {t("productPage.title")}
        </motion.h1>
        {/* subtitle */}
        <motion.p
          className="lg:text-3xl text-lg md:text-2xl mt-4 sm:mt-8 z-10"
          style={{ color: colors.lightCream }}
          initial={{ y: 20, opacity: 0 }}
          whileInView={{
            y: 0,
            opacity: 1,
            transition: { delay: 0.5, duration: 0.6 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          {t("productPage.subTitle")}
        </motion.p>
        {/* icon */}
        <motion.div
          className="mt-24 sm:mt-48"
          initial={{ y: 20, opacity: 0 }}
          whileInView={{
            y: 0,
            opacity: 1,
            transition: { delay: 1.5, duration: 1 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          <SlArrowDown
            className="text-3xl sm:text-4xl text-white animate-bounce hover:text-gray-300 cursor-pointer"
            onClick={scrollDown}
          />
        </motion.div>
      </motion.div>

      {/* Horizontal Divider */}
      <div className="w-full max-h-[20vh] pt-24 pb-24 flex justify-center">
        <hr
          className="w-[23%] sm:w-[15%] md:w-[15%] lg:w-[10%] h-0.5 border-t-0 opacity-100"
          style={{ backgroundColor: colors.darkBrown }}
        />
      </div>

      {/* Products Grid Section */}
      <div className="w-full px-6 py-12 flex flex-col items-center">
        <div className="w-full max-w-6xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {productTypes.map(({ key, path }) => {
            const translatedType = t(`productPage.${key}`);

            return (
              <motion.div
                key={key}
                className="relative w-full h-[350px] cursor-pointer rounded-lg shadow-lg overflow-hidden transform transition-transform duration-300 hover:scale-105"
                initial={{ opacity: 0 }}
                whileInView={{
                  opacity: 1,
                  transition: { delay: 0.2, duration: 0.5 },
                }}
                viewport={{ once: false }}
                onMouseEnter={() => setHoveredProduct(key)}
                onMouseLeave={() => setHoveredProduct(null)}
                onClick={() => navigate(`/products/${path}`)}
              >
                {/* Image */}
                <img
                  src={getProductImageByType(key)}
                  loading="lazy"
                  alt={t(`image_alt.products.${key}`)}
                  className="w-full h-full object-cover rounded-lg"
                />

                {/* Dark overlay and icon on hover */}
                <div
                  className={`absolute inset-0 bg-black transition-opacity duration-500 rounded-lg flex items-center justify-center p-4 ${
                    hoveredProduct === key ? "bg-opacity-50" : "bg-opacity-0"
                  }`}
                >
                  <PiCaretCircleRightLight
                    className={`text-5xl transition-opacity duration-300 ${
                      hoveredProduct === key ? "opacity-100" : "opacity-0"
                    }`}
                    style={{ color: colors.lightCream }}
                  />
                </div>

                {/* Product name below the image */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-25 p-4 rounded-b-lg">
                  <span
                    className="text-xl md:text-2xl font-medium text-center block"
                    style={{ color: colors.lightCream }}
                  >
                    {translatedType}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Horizontal Divider */}
      <div className="w-full max-h-[20vh] pt-24 pb-24 flex justify-center">
        <hr
          className="w-[23%] sm:w-[15%] md:w-[15%] lg:w-[10%] h-0.5 border-t-0 opacity-100"
          style={{ backgroundColor: colors.darkBrown }}
        />
      </div>

      <PortfolioSection />
      <ContactUsSection />
    </div>
  );
};

export default Products;
